import { Check, LoaderCircle, X } from 'lucide-react'
import * as React from 'react'
import { type useSpinDelay } from 'spin-delay'
import { cn } from '#app/utils/misc.tsx'
import { Button, type ButtonProps } from './button.tsx'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from './tooltip.tsx'

export const StatusButton = React.forwardRef<
	HTMLButtonElement,
	ButtonProps & {
		status: 'pending' | 'success' | 'error' | 'idle'
		message?: string | null
		spinDelay?: Parameters<typeof useSpinDelay>[1]
	}
>(({ message, status, className, children, spinDelay, ...props }, ref) => {
	const [currentStatus, setCurrentStatus] = React.useState(status)

	React.useEffect(() => {
		setCurrentStatus(status)
	}, [status])

	let companion = {
		pending:
			props.variant === 'secondary' ? (
				<LoaderCircle width={16} className="animate-spin" />
			) : (
				<LoaderCircle width={16} className="animate-spin" />
			),
		success: <Check width="16" className="" />,
		error: <X width="16" />,
		idle: null,
	}[currentStatus]

	if (currentStatus !== 'idle') {
		setTimeout(() => {
			setCurrentStatus('idle')
		}, 4000)
	}

	return (
		<Button
			ref={ref}
			className={cn(
				'relative flex items-center justify-center gap-2',
				className,
			)}
			{...props}
			disabled={status === 'pending' || props.disabled}
		>
			<div className="flex items-center gap-2">
				<div
					className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${currentStatus !== 'idle' ? 'opacity-100' : 'opacity-0'}`}
				>
					{companion}
				</div>
				<div
					className={`flex items-center gap-2 transition-opacity duration-200 ${currentStatus === 'idle' ? 'opacity-100' : 'opacity-0'}`}
				>
					{children}
				</div>
			</div>
			{message ? (
				<TooltipProvider>
					<Tooltip>
						<TooltipTrigger>{companion}</TooltipTrigger>
						<TooltipContent>{message}</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			) : null}
		</Button>
	)
})
StatusButton.displayName = 'Button'
